// Utility for shared receipt rendering logic
import { formatDateFull } from '../utils/date'

function escapeHtml (text) {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

export function formatDeliveryOption (deliveryOption) {
  if (!deliveryOption) return ''
  const parts = []
  if (deliveryOption.name) {
    parts.push(
      `<div style="font-weight: 600; margin-bottom: 4px;">${escapeHtml(
        deliveryOption.name
      )}</div>`
    )
  }
  if (deliveryOption.description) {
    parts.push(
      `<div style="color: #7C898B; font-size: 13px; margin-bottom: 4px;">${escapeHtml(
        deliveryOption.description
      )}</div>`
    )
  }
  if (
    deliveryOption.info_fields &&
    Array.isArray(deliveryOption.info_fields) &&
    deliveryOption.info_fields.length > 0
  ) {
    const infoHTML = deliveryOption.info_fields
      .map(
        f =>
          `<div style="font-size: 13px;"><strong>${escapeHtml(
            f.label
          )}:</strong> ${escapeHtml(f.value)}</div>`
      )
      .join('')
    parts.push(`<div style="margin-top: 4px;">${infoHTML}</div>`)
  }
  if (
    deliveryOption.available_regions &&
    Array.isArray(deliveryOption.available_regions) &&
    deliveryOption.available_regions.length > 0
  ) {
    parts.push(
      `<div style="font-size: 13px;"><strong>Regions:</strong> ${escapeHtml(
        deliveryOption.available_regions.join(', ')
      )}</div>`
    )
  }
  if (parts.length === 0) return ''
  return `
    <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #E8E8E8;">
      <div style="font-weight: 600; margin-bottom: 8px; color: #253439;">Delivery Option:</div>
      <div style="color: #253439; font-size: 14px; line-height: 1.6;">
        ${parts.join('')}
      </div>
    </div>
  `
}

export function formatAddressSection (
  shippingAddress,
  billingAddress,
  forModal = false
) {
  if (!shippingAddress && !billingAddress) {
    return ''
  }
  const formatAddress = (addr, label) => {
    if (!addr) return ''
    const parts = []
    if (addr.street) parts.push(addr.street)
    const cityStateZip = [addr.city, addr.state, addr.zip]
      .filter(Boolean)
      .join(', ')
    if (cityStateZip) parts.push(cityStateZip)
    if (addr.country) parts.push(addr.country)
    if (parts.length === 0) return ''
    const addressHTML = parts.map(p => `<div>${escapeHtml(p)}</div>`).join('')
    const style = forModal
      ? 'style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #E8E8E8;"'
      : 'style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #E8E8E8;"'
    return `
      <div ${style}>
        <div style="font-weight: 600; margin-bottom: 8px; color: #253439;">${label}:</div>
        <div style="color: #7C898B; font-size: 14px; line-height: 1.6;">
          ${addressHTML}
        </div>
      </div>
    `
  }
  let result = ''
  if (shippingAddress) {
    result += formatAddress(shippingAddress, 'Shipping Address')
  }
  if (billingAddress) {
    result += formatAddress(billingAddress, 'Billing Address')
  }
  return result
}

export function generateReceiptHTML (
  group,
  forModal = false,
  currentUser = null,
  tickets = null
) {
  const logoUrl =
    (typeof window !== 'undefined' ? window.location.origin : '') +
    '/logos/Horizontal%20Logo/No%20Background%20Black%20Logo%20Horizontal.png'
  const companyLogo = `<img src="${logoUrl}" alt="Barry & Bonna Publishing" style="max-width: 100%; height: auto; max-height: 120px; display: block; margin: 0 auto 20px auto;" />`
  const customerName =
    group.customer_name ||
    (currentUser?.first_name && currentUser?.last_name
      ? `${currentUser.first_name} ${currentUser.last_name}`
      : currentUser?.email) ||
    'Customer'
  const orderDate = formatDateFull(group.purchase_date)
  const orderNumber = group.transaction_id
  let mergedItems = [...group.items]
  let ticketArr = tickets
  if (ticketArr) {
    const ticketsForOrder = ticketArr.filter(
      ticket => ticket.transaction_id === group.transaction_id
    )
    for (const ticket of ticketsForOrder) {
      if (
        !mergedItems.some(
          item =>
            item.id === ticket.id && item.event_title === ticket.event_title
        )
      ) {
        mergedItems.push({
          ...ticket,
          price:
            ticket.ticket_price !== undefined
              ? ticket.ticket_price
              : ticket.price !== undefined
              ? ticket.price
              : ticket.total_price !== undefined
              ? ticket.total_price
              : 0,
          quantity: ticket.quantity !== undefined ? ticket.quantity : 1,
          book_title: ticket.event_title,
          book_author: ticket.ticket_holder_name
            ? `${ticket.ticket_holder_name} (Holder)`
            : '',
          format: 'Ticket'
        })
      }
    }
  }
  const itemsHTML = mergedItems
    .map(item => {
      // Use total_price from backend if available (it has correct discount distribution)
      // Fall back to manual calculation only if total_price is not provided
      const itemTotal = item.total_price !== undefined 
        ? parseFloat(item.total_price)
        : parseFloat(item.price) * (item.quantity || 1)
      const quantitySuffix =
        (item.quantity || 1) > 1 ? ` x${item.quantity}` : ''
      const originalPrice = item.original_price
        ? parseFloat(item.original_price)
        : null
      const discountPercentage = item.discount_percentage
        ? parseFloat(item.discount_percentage)
        : null
      const hasDiscount =
        originalPrice &&
        discountPercentage &&
        originalPrice > parseFloat(item.price) &&
        discountPercentage > 0
      const discountInfo = hasDiscount
        ? `
      <div style="color: #2D4D31; font-size: 13px; margin-top: 4px;">
        <span style="text-decoration: line-through; color: #7C898B;">$${originalPrice.toFixed(
          2
        )}</span>
        <span style="margin-left: 8px;">${discountPercentage.toFixed(
          0
        )}% off</span>
      </div>
    `
        : ''
      const authorPrefix = item.format === 'Ticket' ? 'For' : 'By'
      return `
      <tr>
        <td style="padding: 8px 12px; border-bottom: 1px solid #E8E8E8; color: #253439; word-wrap: break-word; overflow-wrap: break-word;">
          <div style="font-weight: 600; margin-bottom: 4px; line-height: 1.3;" data-no-translate>${escapeHtml(
            item.book_title
          )}${quantitySuffix}</div>
          ${
            item.book_author
              ? `<div style="color: #7C898B; font-size: 14px;" data-no-translate>${authorPrefix} ${escapeHtml(
                  item.book_author
                )}</div>`
              : ''
          }
          <div style="color: #7C898B; font-size: 14px;">Format: ${escapeHtml(
            item.format
          )}</div>
          ${discountInfo}
        </td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #E8E8E8; text-align: center; color: #253439; word-wrap: break-word;">${
          item.quantity || 1
        }</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #E8E8E8; text-align: right; color: #253439; word-wrap: break-word;" data-no-translate>$${parseFloat(
          item.price
        ).toFixed(2)}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #E8E8E8; text-align: right; color: #253439; font-weight: 600; word-wrap: break-word;" data-no-translate>$${itemTotal.toFixed(
          2
        )}</td>
      </tr>
    `
    })
    .join('')
  // Use backend-calculated subtotal when available, or sum item totals (with discount distribution)
  const itemsSubtotal = mergedItems.reduce((sum, item) => {
    const itemTotal = item.total_price !== undefined
      ? parseFloat(item.total_price)
      : parseFloat(item.price) * (item.quantity || 1)
    return sum + itemTotal
  }, 0)
  const subtotal = group.subtotal > 0 ? group.subtotal : itemsSubtotal
  const finalTotal =
    group.total > 0 ? group.total : subtotal - (group.discount_amount || 0)
  const discountRow =
    group.discount_amount > 0
      ? `
    <tr>
      <td colspan="3" style="padding: 8px 12px; text-align: right; color: #253439;">Discount${
        group.discount_code
          ? ` (<span data-no-translate>${escapeHtml(
              group.discount_code
            )}</span>)`
          : ''
      }:</td>
      <td style="padding: 8px 12px; text-align: right; color: #2D4D31; font-weight: 600;" data-no-translate>-$${group.discount_amount.toFixed(
        2
      )}</td>
    </tr>
  `
      : ''

  const receiptContent = `
    <div class="receipt-container" style="font-family: 'Georgia', serif; color: #253439; max-width: 800px; margin: 0 auto; padding: 40px 20px;">
      <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #2D4D31;">
        ${companyLogo}
        <h1 style="color: #2D4D31; font-size: 24px; margin: 10px 0;">Receipt</h1>
      </div>
      <div style="margin-bottom: 30px;">
        <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Order Number:</strong> ${orderNumber}</p>
        <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Order Date:</strong> ${orderDate}</p>
        <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Customer:</strong> ${escapeHtml(
          customerName
        )}</p>
        ${
          currentUser?.email
            ? `<p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Email:</strong> ${escapeHtml(
                currentUser.email
              )}</p>`
            : ''
        }
        ${formatAddressSection(
          group.shipping_address,
          group.billing_address,
          true
        )}
        ${formatDeliveryOption(group.delivery_option)}
      </div>
      <table class="receipt-table" style="width: 100%; border-collapse: collapse; margin: 20px 0; table-layout: auto;">
        <thead>
          <tr>
            <th style="background-color: #FAFAFA; padding: 8px 12px; text-align: left; border-bottom: 2px solid #E8E8E8; color: #253439; font-weight: 600; word-wrap: break-word; width: 40%;">Item</th>
            <th style="background-color: #FAFAFA; padding: 8px 12px; text-align: center; border-bottom: 2px solid #E8E8E8; color: #253439; font-weight: 600; word-wrap: break-word; width: 15%;">Qty</th>
            <th style="background-color: #FAFAFA; padding: 8px 12px; text-align: right; border-bottom: 2px solid #E8E8E8; color: #253439; font-weight: 600; word-wrap: break-word; width: 22.5%;">Unit Price</th>
            <th style="background-color: #FAFAFA; padding: 8px 12px; text-align: right; border-bottom: 2px solid #E8E8E8; color: #253439; font-weight: 600; word-wrap: break-word; width: 22.5%;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHTML}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="3" style="padding: 8px 12px; text-align: right; color: #253439; word-wrap: break-word;">Subtotal:</td>
            <td style="padding: 8px 12px; text-align: right; color: #253439; word-wrap: break-word;" data-no-translate>$${subtotal.toFixed(
              2
            )}</td>
          </tr>
          ${discountRow}
          <tr style="border-top: 2px solid #253439;">
            <td colspan="3" style="padding: 8px 12px; text-align: right; color: #253439; font-size: 18px; font-weight: 600; word-wrap: break-word;">Total:</td>
            <td style="padding: 8px 12px; text-align: right; color: #253439; font-size: 18px; font-weight: 600; word-wrap: break-word;" data-no-translate>$${finalTotal.toFixed(
              2
            )}</td>
          </tr>
        </tfoot>
      </table>
      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #E8E8E8; text-align: center; color: #7C898B; font-size: 12px;">
        <p style="margin: 5px 0;">Thank you for your purchase!</p>
        <p style="margin: 5px 0;">© ${new Date().getFullYear()} <i>Barry & Bonna Publishing</i> LLC. All rights reserved.</p>
      </div>
    </div>
  `

  if (forModal) {
    return receiptContent
  }

  // Full HTML document for PDF generation
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Receipt - ${orderNumber}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Georgia', serif;
          color: #253439;
          background: white;
          padding: 20px;
        }
        
        /* Print-specific styles for PDF generation */
        @media print {
          body {
            padding: 0;
            margin: 0;
          }
          
          /* Table formatting for better PDF rendering */
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            table-layout: auto !important;
            page-break-inside: auto;
          }
          
          /* Ensure table headers repeat on each page */
          thead {
            display: table-header-group !important;
          }
          
          tbody {
            display: table-row-group !important;
          }
          
          tfoot {
            display: table-footer-group !important;
          }
          
          /* Prevent page breaks inside table headers and important rows */
          thead tr {
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
          }
          
          tfoot tr {
            page-break-inside: avoid !important;
            page-break-before: avoid !important;
          }
          
          /* Allow proper page breaks for table rows */
          tbody tr {
            page-break-inside: avoid;
          }
          
          /* Column width optimization */
          th:nth-child(1), td:nth-child(1) { width: 40% !important; } /* Item */
          th:nth-child(2), td:nth-child(2) { width: 15% !important; } /* Qty */
          th:nth-child(3), td:nth-child(3) { width: 22.5% !important; } /* Unit Price */
          th:nth-child(4), td:nth-child(4) { width: 22.5% !important; } /* Total */
          
          /* Text wrapping and overflow handling */
          th, td {
            word-wrap: break-word !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
            hyphens: auto !important;
            max-width: 0 !important;
          }
          
          /* Ensure no content gets cut off */
          .receipt-container {
            page-break-inside: auto;
          }
        }
        
        /* General table improvements */
        table {
          border-collapse: collapse;
          table-layout: auto;
        }
        
        th, td {
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
        }
      </style>
    </head>
    <body>
      ${receiptContent}
    </body>
    </html>
  `
}
